'use client';
import { LineItem } from '@repo/ui/src/types/lineItem';
import React, { useState } from 'react';
import Quantity from '@components/Cart/_Quantity';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import ProductPricing from './_ProductPricing';
import Image from '@repo/ui/src/components/Image';
import { useDebounceCallback } from '@hooks/useDebouncedCallback';
import useUpdateBasket from '@hooks/basket/useUpdateBasket';
import LoadingLogo from '@components/Loading/LoadingLogo';
import ExtendedWarrantyMenu from './_ExtendedWarrantyMenu';
import useAddToBasket from '@hooks/basket/useAddToBasket';

export type UpdateType = 'stepper' | 'remove';

export type HandleChange = (newQuantity: number) => void;

interface PropsWithoutWarranty {
  lineItem: LineItem;
  outOfStock?: boolean;
  isWarranty?: false;
  parent?: never;
}

interface PropsWithWarranty {
  lineItem: LineItem;
  outOfStock?: never;
  isWarranty: true;
  parent: LineItem;
}

const SingleLineItem = ({
  lineItem,
  outOfStock,
  isWarranty,
  parent,
}: PropsWithoutWarranty | PropsWithWarranty) => {
  const { trigger: updateBasket, isMutating: isLoadingUpdate } = useUpdateBasket();
  const { trigger: addToBasket, isMutating: isLoadingAdd } = useAddToBasket();
  const [updateType, setUpdateType] = useState<UpdateType>();
  const { t } = useTranslation();
  const handleChange = useDebounceCallback(
    (newQuantity: number, type: UpdateType) => {
      const hasWarrantyOptions = !!lineItem.warrantyOptions.length;
      if (newQuantity !== 0 && hasWarrantyOptions) {
        return Promise.all(
          new Array(newQuantity - 1).fill(null).map(() => {
            addToBasket([
              {
                ...lineItem,
                optionItems: [
                  {
                    optionId: 'warranty',
                    optionValueId: lineItem.attachedWarranty?.optionValueId ?? '',
                  },
                ],
              },
            ]);
          }),
        );
      }

      if (isWarranty) {
        updateBasket({
          lineItem: parent,
          quantity: parent.quantity,
          optionItems: [{ optionId: 'warranty', optionValueId: parent!.warrantyDefaultOption!.id }],
        });
      } else {
        updateBasket({ lineItem, quantity: newQuantity });
      }
      setUpdateType(type);
    },
    [lineItem, isWarranty],
    500,
  );

  if (!lineItem) return null;
  const Element = isWarranty ? 'div' : 'li';

  return (
    <Element
      className={classNames(
        !isWarranty && 'tw-border-b tw-border-surface-200 tw-grid tw-gap-y-8 tw-py-6',
      )}
      data-testid={isWarranty ? 'warranty-line-item' : 'product-line-item'}
    >
      <div className="tw-flex tw-flex-col" role="group" aria-label="product">
        {outOfStock && (
          <div className="tw-bg-alert-400 tw-px-4 tw-py-2 tw-mb-6">
            <p className="tra-text-web-body-m tw-text-white">
              {t('cart.outOfStockText', undefined, {
                default:
                  'This item is out of stock. Remove from your cart to complete your purchase.',
              })}
            </p>
          </div>
        )}
        <div
          className={classNames(
            'tw-w-full tw-grid tw-gap-x-6',
            !isWarranty ? 'tw-grid-cols-[20%_3fr_1fr]' : 'tw-grid-cols-[14%_3fr_1fr]',
          )}
        >
          <div className={classNames('tw-relative', outOfStock && 'tw-opacity-50')}>
            <Image
              src={
                isWarranty
                  ? '/images/shield.png'
                  : lineItem?.getMainImageSize(isWarranty ? 62 : 96, 1, 1)
              }
              alt=""
              layout="responsive"
              width={62}
              height={62}
            />
          </div>

          <div className={classNames('tw-w-full', outOfStock && 'tw-opacity-50')}>
            <div>
              <h5
                className={classNames(
                  'tw-text-left tw-font-bold',
                  isWarranty ? 'tra-text-web-body-m' : 'tra-text-web-body-l',
                )}
              >
                {lineItem?.title}
              </h5>
              <div className="tw-flex tw-flex-col">
                <Quantity
                  editable={!isWarranty}
                  lineItem={lineItem}
                  isLoading={isLoadingUpdate || isLoadingAdd}
                  handleChange={handleChange}
                  outOfStock={outOfStock}
                />
              </div>
            </div>
          </div>

          <div className="tw-flex tw-flex-col tw-items-end tw-justify-between sm:tw-justify-start">
            <div className="tw-h-6">
              {isLoadingUpdate && updateType == 'stepper' ? (
                <LoadingLogo />
              ) : (
                !outOfStock && <ProductPricing item={lineItem} editable={!isWarranty} />
              )}
            </div>

            {!lineItem.bonus &&
              (isLoadingUpdate && updateType === 'remove' ? (
                <LoadingLogo />
              ) : (
                <>
                  {outOfStock && (
                    <p className="tra-text-web-body-l tw-overflow-hidden tw-whitespace-nowrap tw-bold tw-opacity-50">
                      {t('productDetailPageContent.outOfStockText', undefined, {
                        default: 'Out of Stock',
                      })}
                    </p>
                  )}
                  <button
                    className={classNames(
                      'tw-underline tra-text-web-body-m',
                      outOfStock ? 'tw-mt-0' : 'tw-mt-7 ',
                    )}
                    data-testid={!parent ? 'c-remove-btn' : 'c-remove-warranty-btn'}
                    onClick={() => handleChange(0, 'remove')}
                  >
                    {t('cart.cartRemoveText', undefined, { default: 'Remove' })}
                  </button>
                </>
              ))}
          </div>
        </div>
      </div>
      {!!lineItem.warrantyExtendedOptions?.length && !lineItem.isExtendedWarrantyAttached && (
        <ExtendedWarrantyMenu lineItem={lineItem} />
      )}
      {lineItem.isExtendedWarrantyAttached && (
        <SingleLineItem lineItem={lineItem.attachedWarranty!} isWarranty parent={lineItem} />
      )}
    </Element>
  );
};

export default SingleLineItem;
